import {
  Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges,
} from '@angular/core';

interface CalendarDay {
  iso: string;
  dayNumber: number;
  inMonth: boolean;
  isPast: boolean;
  isToday: boolean;
  isSelected: boolean;
  hasConflict: boolean;
}

/**
 * Lightweight multi-select calendar:
 *  - 6-week grid for the visible month.
 *  - Dates before `minDate` (defaults to today) and out-of-month days are unselectable.
 *  - When `minDate` is in the past the calendar allows navigation back to that month,
 *    enabling retroactive date selection within the current school year.
 *  - Toggling a date emits the full updated selection.
 *  - Highlights conflicting dates supplied by the parent.
 */
@Component({
  selector: 'app-multi-date-calendar',
  templateUrl: './multi-date-calendar.component.html',
  styleUrls: [ './multi-date-calendar.component.scss' ],
})
export class MultiDateCalendarComponent implements OnInit, OnChanges {
  @Input() selectedDates: string[] = [];
  @Input() conflictingDates: string[] | Set<string> = new Set<string>();
  /**
   * Earliest selectable date in YYYY-MM-DD format.  When provided and earlier than
   * today (e.g. school-year start), the calendar allows backward navigation to that
   * month and marks only dates before this value as disabled.  Defaults to today.
   */
  @Input() minDate: string = '';

  @Output() datesChange = new EventEmitter<string[]>();

  weekdayLabels = [
    'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
  ];
  monthLabels = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  visibleYear: number;
  visibleMonth: number;

  weeks: CalendarDay[][] = [];

  todayIso = '';

  ngOnInit(): void {
    const now = new Date();
    this.visibleYear = now.getFullYear();
    this.visibleMonth = now.getMonth();
    this.todayIso = this.toIso(now);
    this.rebuild();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDates'] || changes['conflictingDates'] || changes['minDate']) {
      this.rebuild();
    }
  }

  // #region rendering
  private rebuild = () => {
    const conflicts = this.conflictsAsSet();
    const selected = new Set(this.selectedDates || []);
    const grid: CalendarDay[][] = [];

    const firstOfMonth = new Date(this.visibleYear, this.visibleMonth, 1);
    const startWeekday = firstOfMonth.getDay();

    const startDate = new Date(firstOfMonth);
    startDate.setDate(firstOfMonth.getDate() - startWeekday);

    const minDateKey = (this.minDate && /^\d{4}-\d{2}-\d{2}$/.test(this.minDate))
      ? this.minDate
      : this.todayIso;

    for (let week = 0; week < 6; week++) {
      const weekRow: CalendarDay[] = [];
      for (let day = 0; day < 7; day++) {
        const dateForCell = new Date(startDate);
        dateForCell.setDate(startDate.getDate() + (week * 7) + day);

        const iso = this.toIso(dateForCell);
        const inMonth = dateForCell.getMonth() === this.visibleMonth;
        const isPast = iso < minDateKey;
        const isToday = iso === this.todayIso;

        weekRow.push({
          iso,
          dayNumber: dateForCell.getDate(),
          inMonth,
          isPast,
          isToday,
          isSelected: selected.has(iso),
          hasConflict: conflicts.has(iso),
        });
      }
      grid.push(weekRow);
    }

    this.weeks = grid;
  }

  private conflictsAsSet = (): Set<string> => {
    if (this.conflictingDates instanceof Set) return this.conflictingDates;
    return new Set(this.conflictingDates || []);
  }

  private toIso = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  // #endregion rendering

  // #region navigation

  /** Returns the year/month of the earliest selectable date as { year, month }. */
  private get minDateYearMonth(): { year: number; month: number } {
    if (this.minDate && /^\d{4}-\d{2}-\d{2}$/.test(this.minDate)) {
      const [ y, m ] = this.minDate.split('-').map(Number);
      return { year: y, month: m - 1 };
    }
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  }

  goPreviousMonth = () => {
    if (this.isPreviousDisabled()) return;

    if (this.visibleMonth === 0) {
      this.visibleMonth = 11;
      this.visibleYear -= 1;
    } else {
      this.visibleMonth -= 1;
    }
    this.rebuild();
  }

  goNextMonth = () => {
    if (this.visibleMonth === 11) {
      this.visibleMonth = 0;
      this.visibleYear += 1;
    } else {
      this.visibleMonth += 1;
    }
    this.rebuild();
  }

  isPreviousDisabled = (): boolean => {
    const { year: minYear, month: minMonth } = this.minDateYearMonth;
    return this.visibleYear < minYear
      || (this.visibleYear === minYear && this.visibleMonth <= minMonth);
  }

  get monthLabel(): string {
    return `${this.monthLabels[this.visibleMonth]} ${this.visibleYear}`;
  }
  // #endregion navigation

  // #region selection
  toggleDay = (cell: CalendarDay) => {
    if (cell.isPast || !cell.inMonth) return;
    const next = new Set(this.selectedDates || []);
    if (next.has(cell.iso)) {
      next.delete(cell.iso);
    } else {
      next.add(cell.iso);
    }
    const sorted = Array.from(next).sort();
    this.datesChange.emit(sorted);
  }
  // #endregion selection
}
